import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Loader2, Zap, RefreshCw, Info } from 'lucide-react';
import toast from 'react-hot-toast';

import {
  getOptimizerPool,
  confirmAllocation,
  rebuildPool,
} from '../../../../../../../services/staffingService';

import CoverageBar from '../../../shared/CoverageBar';
import OfficerLocationMap from '../../../shared/OfficerLocationMap';
import ShiftAllocationTable from './ShiftAllocationTable';

const OFFER_STATUS = {
  open:      { label: 'Offered',   colors: 'bg-amber-50 text-amber-700 border-amber-200'      },
  accepted:  { label: 'Assigned',  colors: 'bg-sky-50 text-sky-700 border-sky-200'            },
  active:    { label: 'Active',    colors: 'bg-blue-50 text-blue-700 border-blue-200'          },
  completed: { label: 'Completed', colors: 'bg-gray-50 text-gray-600 border-gray-200'          },
  disputed:  { label: 'Disputed',  colors: 'bg-red-50 text-red-700 border-red-200'             },
  paid:      { label: 'Paid',      colors: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const ACTIVE_OFFER_STATUSES = ['open', 'accepted', 'active'];

// ─────────────────────────────────────────────────────────────────────────────
// MASTER COMPONENT: Centralized state & data flow
// ─────────────────────────────────────────────────────────────────────────────

const ShiftsAssignTabMaster = ({ job, shifts, offersMetadata, onRefresh }) => {
  const { providerCompanyId, jobId } = useParams();
  const history = useHistory();

  // ────────── STATE ──────────────
  // Optimizer pool: { [shiftId]: [ recommendationDoc, ... ] }
  const [poolByShift, setPoolByShift] = useState(null);
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolComputing, setPoolComputing] = useState(false); // large-agency async Temporal build
  // 'no_officers' | 'computing' | 'empty' | 'ready' | null
  const [poolStatus, setPoolStatus] = useState(null);

  // Stage-2 proximity pool — all agency officers tagged with eligibility reasons
  const [proximityPool, setProximityPool] = useState([]);

  // Allocation mode from pool response
  const [autoMode, setAutoMode] = useState(false);

  // Tracks which (shiftId-officerId) is currently having an offer sent
  const [sendingKey, setSendingKey] = useState(null);

  // Map interaction state
  const [selectedOfficer, setSelectedOfficer] = useState(null);

  // ────────── COMPUTED DATA ──────────────

  // Map: shiftId → ALL offers for that shift (grouped)
  const offersByShift = useMemo(() => {
    const acc = {};
    (offersMetadata || []).forEach((o) => {
      const key = o.shifts?.[0]?.shiftId?.toString();
      if (!key) return;
      if (!acc[key]) acc[key] = [];
      acc[key].push(o);
    });
    return acc;
  }, [offersMetadata]);

  // Assigned officers (filtered by ACTIVE status)
  const assignedOfficers = useMemo(() => {
    return Object.entries(offersByShift)
      .flatMap(([shiftId, offers]) =>
        offers
          .filter(o => ACTIVE_OFFER_STATUSES.includes(o.status))
          .map(o => ({ ...o, shiftId }))
      );
  }, [offersByShift]);

  const assignedOfficerIds = useMemo(() => {
    const ids = new Set();
    assignedOfficers.forEach((offer) => {
      const providerResource = offer?.provider_resource_id;
      const id = (providerResource?._id || providerResource)?.toString();
      if (id) ids.add(id);
    });
    return ids;
  }, [assignedOfficers]);

  const assignedMapOfficers = useMemo(() => {
    const acc = [];
    const seen = new Set();

    assignedOfficers.forEach((offer) => {
      const providerResource = offer?.provider_resource_id;
      const resourceId = providerResource?._id || providerResource;
      const key = resourceId?.toString();
      if (!key || seen.has(key)) return;

      const geo = providerResource?.geoLocation;
      if (!geo?.coordinates || geo.coordinates.length < 2) return;

      const [lng, lat] = geo.coordinates;
      const user = providerResource?.user_id || {};

      seen.add(key);
      acc.push({
        resourceId,
        lng,
        lat,
        firstName: user.firstName || '—',
        lastName: user.lastName || '',
        email: user.email,
        profilePictureUrl: user.profilePictureUrl,
        proposedRate: offer?.negotiations?.providerHourlyRate ?? 0,
        offerId: offer?._id,
        isAssigned: true,
      });
    });

    return acc;
  }, [assignedOfficers]);

  // Pool candidates — ranked officers from optimizer pool, excluding already-assigned
  const allCandidates = useMemo(() => {
    if (!poolByShift) return [];

    const seen = new Set();
    const acc = [];

    for (const candidates of Object.values(poolByShift)) {
      for (const rec of candidates) {
        if (rec.status !== 'pending') continue;

        const resource = rec.resourceId;
        const resourceId = resource?._id || rec.resourceId;
        const key = resourceId?.toString();
        if (!key || seen.has(key)) continue;
        if (assignedOfficerIds.has(key)) continue;

        seen.add(key);

        const geo = resource?.geoLocation;
        const [lng, lat] = geo?.coordinates || [];
        const user = resource?.user_id || {};

        acc.push({
          resourceId,
          lng: lng ?? null,
          lat: lat ?? null,
          firstName: user.firstName || resource?.firstName || '—',
          lastName: user.lastName || resource?.lastName || '',
          email: user.email,
          profilePictureUrl: user.profilePictureUrl,
          proposedRate: rec.proposedRate ?? 0,
          distanceMiles: rec.distanceMiles,
          rank: rec.rank,
          score: rec.score,
          phase: rec.phase,
          offerId: rec._id, // recommendation doc _id used as stable key
          isAssigned: false,
        });
      }
    }

    // Sort by rank so best candidates appear first
    return acc.sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
  }, [poolByShift, assignedOfficerIds]);

  // Map gets: assigned (blue) + smart matches (pink) + other officers (gray, with coords only)
  const proximityMapOfficers = useMemo(() => {
    const smartMatchIds = new Set(allCandidates.map(c => c.resourceId?.toString()));
    return (proximityPool || [])
      .filter(o => {
        const rid = o.resourceId?.toString();
        if (!rid) return false;
        if (assignedOfficerIds.has(rid)) return false;
        if (smartMatchIds.has(rid)) return false;
        const coords = o.geoLocation?.coordinates;
        return coords?.[0] && coords?.[1];
      })
      .map(o => {
        const [lng, lat] = o.geoLocation.coordinates;
        return {
          resourceId: o.resourceId,
          lng,
          lat,
          firstName: o.firstName,
          lastName: o.lastName,
          email: o.email,
          profilePictureUrl: o.profilePictureUrl,
          proposedRate: o.minHourlyRate ?? 0,
          distanceMiles: o.distanceMiles,
          isAssigned: false,
          isOther: true,
          reasons: o.reasons,
        };
      });
  }, [proximityPool, allCandidates, assignedOfficerIds]);

  const mapOfficers = useMemo(
    () => [...assignedMapOfficers, ...allCandidates, ...proximityMapOfficers],
    [assignedMapOfficers, allCandidates, proximityMapOfficers]
  );

  // Job coordinates for map
  const jobCoordinates = useMemo(() => {
    return job?.geoLocation?.coordinates;
  }, [job]);

  // Coverage metrics
  const stats = useMemo(() => {
    return job?.statistics || {};
  }, [job]);

  const covered = useMemo(() => stats.totalShiftsCovered ?? 0, [stats]);
  const total = useMemo(() => stats.totalStaffRequired ?? shifts.reduce((s, sh) => s + (sh.staffsRequired || 1), 0), [stats, shifts]);

  // ────────── HANDLERS ──────────────

  const loadPool = useCallback(async () => {
    setPoolLoading(true);
    setPoolComputing(false);
    setPoolStatus(null);
    try {
      const resp = await getOptimizerPool(providerCompanyId, jobId);
      // 202 = large-agency async Temporal build still in progress
      if (resp.status === 202 || resp.data?.status === 'computing') {
        setPoolComputing(true);
        setPoolByShift({});
        setProximityPool(resp.data?.proximityPool || []);
        return;
      }
      const { pool, proximityPool: prox, job: jobMeta, status } = resp.data;
      setPoolStatus(status || 'ready');
      setPoolByShift(pool || {});
      setProximityPool(prox || []);
      setAutoMode(jobMeta?.allocationMode === 'auto');
    } catch {
      toast.error('Couldn\'t load matches — please refresh');
    } finally {
      setPoolLoading(false);
    }
  }, [providerCompanyId, jobId]);

  const handleRebuildPool = useCallback(async () => {
    try {
      setPoolLoading(true);
      const resp = await rebuildPool(providerCompanyId, jobId);
      setPoolComputing(true);
      setPoolByShift({});
      setProximityPool(resp.data?.proximityPool || []);
      setPoolStatus(null);
      toast.success('Re-running matching — recommendations will appear shortly');
    } catch {
      toast.error('Couldn\'t restart matching — please try again');
    } finally {
      setPoolLoading(false);
    }
  }, [providerCompanyId, jobId]);

  const handleSendSingleOffer = useCallback(async (shiftId, rec) => {
    const officerId = rec.resourceId?._id?.toString() || rec.resourceId?.toString();
    const key = `${shiftId}-${officerId}`;
    setSendingKey(key);
    try {
      const payload = [{ shiftId, officerId, proposedRate: rec.proposedRate ?? job?.hourlyPayRate ?? 0 }];
      const resp = await confirmAllocation(providerCompanyId, jobId, payload);
      const { sent, skipped } = resp.data;
      const firstName = rec.resourceId?.user_id?.firstName || 'Officer';
      if (sent > 0) toast.success(`Offer sent to ${firstName}`);
      if (skipped?.length > 0) {
        skipped.forEach(({ reason }) => toast(`${firstName}: ${reason}`, { icon: '⚠️' }));
      }
      await Promise.all([loadPool(), onRefresh()]);
    } catch {
      toast.error('Failed to send offer');
    } finally {
      setSendingKey(null);
    }
  }, [job, providerCompanyId, jobId, loadPool, onRefresh]);

  const handleOpenOfferDetail = useCallback((offer) => {
    const shiftId = offer?.shiftId || offer?.shifts?.[0]?.shiftId;
    const offerId = offer?._id;
    if (!shiftId || !offerId) return;
    history.push(`/provider-company/${providerCompanyId}/dispatch/shift/${shiftId}/offer/${offerId}`);
  }, [history, providerCompanyId]);

  const handleSelectAssignedOfficer = useCallback((offer) => {
    const providerResource = offer?.provider_resource_id;
    const geo = providerResource?.geoLocation;
    if (!geo?.coordinates || geo.coordinates.length < 2) {
      toast.error('No location found for this assigned officer');
      return;
    }

    const [lng, lat] = geo.coordinates;
    const selected = {
      resourceId: providerResource?._id || providerResource,
      lng,
      lat,
      firstName: providerResource?.user_id?.firstName || '—',
      lastName: providerResource?.user_id?.lastName || '',
      email: providerResource?.user_id?.email,
      profilePictureUrl: providerResource?.user_id?.profilePictureUrl,
      proposedRate: offer?.negotiations?.providerHourlyRate ?? 0,
      offerId: offer?._id,
      isAssigned: true,
    };

    const selectedId = selectedOfficer?.resourceId?.toString();
    const currentId = selected.resourceId?.toString();
    setSelectedOfficer(selectedId === currentId ? null : selected);
  }, [selectedOfficer]);

  // Load pool on mount
  useEffect(() => { loadPool(); }, [loadPool]);

  // Auto-poll every 3 s while the large-agency Temporal build is running
  useEffect(() => {
    if (!poolComputing) return;
    const interval = setInterval(async () => {
      try {
        const resp = await getOptimizerPool(providerCompanyId, jobId);
        if (resp.status === 202 || resp.data?.status === 'computing') return;
        const { pool, proximityPool: prox, job: jobMeta, status } = resp.data;
        setPoolByShift(pool || {});
        setProximityPool(prox || []);
        setPoolStatus(status || 'ready');
        setAutoMode(jobMeta?.allocationMode === 'auto');
        setPoolComputing(false);
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [poolComputing, providerCompanyId, jobId]);

  // ────────── RENDER ──────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">Assign Officers to Shifts</p>
          <div className="mt-1 w-48">
            <CoverageBar covered={covered} total={total} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          {autoMode && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 border border-emerald-200">
              <Zap size={11} />
              Auto-allocation active
            </span>
          )}
          <button
            onClick={loadPool}
            disabled={poolLoading}
            className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {poolLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
          <button
            onClick={handleRebuildPool}
            disabled={poolLoading}
            className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <Zap size={12} />
            Re-run Matching
          </button>

        </div>
      </div>

      {poolLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={20} className="animate-spin text-brand-pink mr-3" />
          <span className="text-sm text-muted-foreground">Finding your best matches…</span>
        </div>
      ) : poolStatus === 'no_officers' ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-14 text-center shadow-sm">
          <Info size={28} className="text-muted-foreground" />
          <div>
            <p className="text-sm font-semibold text-foreground">No officers registered yet</p>
            <p className="mt-1 text-xs text-muted-foreground max-w-xs">
              Add officers from the Officers tab so we can start finding recommended matches for your shifts.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Computing banner — large-agency Temporal build still running */}
          {poolComputing && (
            <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <Loader2 size={14} className="animate-spin text-amber-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-amber-700">Finding your best matches</p>
                <p className="text-[11px] text-amber-600">Scoring officers by proximity, schedule fit, and rate — usually done in seconds. Your full roster is shown below in the meantime.</p>
              </div>
            </div>
          )}

          {/* Empty-pool banner — optimizer ran but found 0 eligible candidates */}
          {poolStatus === 'empty' && !poolComputing && (
            <div className="flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3">
              <Info size={14} className="text-sky-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-sky-700">No recommended matches for this job</p>
                <p className="text-[11px] text-sky-600">Officers may not meet the rate or distance requirements for this shift. You can still send offers manually using the roster below.</p>
              </div>
              <button
                onClick={handleRebuildPool}
                disabled={poolLoading}
                className="flex-shrink-0 text-[10px] font-medium text-sky-600 hover:text-sky-800 underline"
              >
                Try again
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* LEFT COLUMN: Tables + List */}
            <div className="lg:col-span-2 space-y-6">
              <ShiftAllocationTable
                shifts={shifts}
                offersByShift={offersByShift}
                poolByShift={poolByShift || {}}
                proximityPool={proximityPool}
                assignedOfficerIds={assignedOfficerIds}
                OFFER_STATUS={OFFER_STATUS}
                ACTIVE_OFFER_STATUSES={ACTIVE_OFFER_STATUSES}
                onOpenOffer={handleOpenOfferDetail}
                onSelectAssignedOfficer={handleSelectAssignedOfficer}
                onSelectOfficer={setSelectedOfficer}
                onSendOffer={handleSendSingleOffer}
                sendingKey={sendingKey}
                selectedOfficer={selectedOfficer}
              />
            </div>

            {/* RIGHT COLUMN: MAPBOX MAP */}
            <div className="lg:col-span-1 h-[600px] sticky top-20">
              <OfficerLocationMap
                jobCoordinates={jobCoordinates}
                officers={mapOfficers}
                selectedOfficer={selectedOfficer}
                onSelectOfficer={setSelectedOfficer}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftsAssignTabMaster;
